'use client'

/**
 * HOME PERSONALISED_PRODUCTS PL widget — widget-of-widget container.
 *
 * v2 nested pattern (see FEATURED_PRODUCTS_SHOWCASE / the home IRL pair): the page renderer
 * pre-renders the nested SINGLE_PRODUCT_CARD children (the shared self-rendering leaf, DS/legacy
 * routing built in) and hands them in via `renderedChildren`; this container decides how to
 * arrange them. Like the non-PL `PersonalisedProducts` section it branches on
 * `shouldUseDesignSystem()`: the DS chrome reuses `personalised-products-ds`'s exported classes
 * (shell / header / 6-up grid + test locator), the legacy chrome mirrors
 * `PersonalisedProductsLegacy` (same scss module, header row + shop button / mobile arrow link,
 * mobile products-list section class). List-level concerns are shared by both branches — the
 * device-specific product cap (12 desktop / 8 mobile defaults), the `__personalised_product_feed`
 * trackers, scroll-into-view visibility telemetry, batched product-view analytics, and the header
 * CTA tracking.
 *
 * The recommendation feed is resolved at PL serve time (productFeedType / productFeedLimit →
 * RECOMMENDATION datasource) and arrives enriched as nested SINGLE_PRODUCT_CARD widgets, so the
 * non-PL section's client-side feed fetch has no PL equivalent (no loader/skeleton state).
 * Section-level proto → domain conversion is delegated to `buildPersonalisedProductsConfig`.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { Button as DSButton } from '@lastbrand/web-ui/button'
import { ChevronRightIcon } from '@lastbrand/web-ui/icons'
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useMemo } from 'react'

import {
  chevronLinkClass,
  DEFAULT_PRODUCTS_LIMIT_FOR_DESKTOP,
  DEFAULT_PRODUCTS_LIMIT_FOR_MOBILE,
  gridClass,
  headerRowClass,
  sectionInnerClass,
  sectionShellClass,
  titleSlotClass,
} from '@src/components/blocks/personalised-products-ds/personalised-products-ds'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Button, Icon, Link, RichText } from '@src/quince-components/src'
import TrackedLink from '@src/quince-components/src/atoms/link'
import ButtonDomain from '@src/quince-site-common/src/domains/Button'
import RichTextDomain from '@src/quince-site-common/src/domains/RichText'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useDynamicStyle from '@src/quince-site-common/src/hooks/useDynamicStyle'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import RightArrowIcon from '@src/quince-site-integrated/src/assets/right-carousel-arrow.svg'
import productTileStyles from '@src/SectionRenderer/sections/commonStyles/newHomePageProductTile.module.scss'
import styles from '@src/SectionRenderer/sections/personalisedProducts/personalisedProducts.module.scss'
import {
  getContentContainerTrackingMetadata,
  getTrackingContext,
} from '@src/SectionRenderer/sections/productListSection/productListSection.helpers'
import { FINDING_METHOD_AGGREGATE, getFindingMethodAggregate, setFindingMethod } from '@src/telemetry/findingMethod'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { CardDisplayOptionsProvider, type CardChromeProps } from '../single-product-card/card-display-options-context'
import { CardTrackingProvider } from '../single-product-card/card-tracking-context'

import { buildPersonalisedProductsConfig } from './personalised-products-section-builders'

import type {
  PersonalisedProductsWidgetData,
  ProductCardData,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

// @i18n-ignore
const NAVIGATION = 'navigation'
// @i18n-ignore
const CLICK_SHOP_BUTTON = 'click__shop__button'

// Legacy-card tile styling for the 6-up home grid — mirrors the non-PL
// `personalised-products-legacy` section, which passes these `newHomePageProductTile`
// classes to the shared `ProductCard`. The DS card sizes itself via `dsVariant`, but on
// the legacy path `dsVariant` is a no-op, so without these the legacy card falls back to
// its wider default typography and default heart position (larger text + misplaced
// wishlist icon vs the BFF render). Published on the leaf via `cardProps`.
const HOME_TILE_CHROME_PROPS: CardChromeProps = {
  titleClassName: productTileStyles.title,
  productPriceClass: productTileStyles.price,
  wishlistIconClassName: productTileStyles.wishlistWrapper,
  wishlistIconClass: productTileStyles.wishlistIcon,
  ratingTextClassName: productTileStyles.ratingText,
  excessOptionsTextClassName: productTileStyles.excessOptionsText,
}

// Widget.widgets is a recursive proto type TS can't resolve through Omit<Widget,'data'>;
// read nested SINGLE_PRODUCT_CARD data via a narrow cast (same pattern as the other home containers).
interface ProductNestedWidget {
  data?: { productItem?: ProductCardData }
}

function PersonalisedProductsWidgetComponent(props: PLTypedWidgetProps<PersonalisedProductsWidgetData>) {
  const data = props.data.personalisedProductsData

  const { t } = useTranslation('common')
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

  const nestedWidgets = (props as unknown as { widgets?: ProductNestedWidget[] }).widgets
  const renderedChildren = props.renderedChildren

  // Section-level proto → domain values (heading, CTA buttons, caps, analytics) via the shared
  // builder — product cards render as pre-rendered children, so none are passed here.
  const sectionConfig = useMemo(() => (data ? buildPersonalisedProductsConfig(data, []) : null), [data])
  const {
    richTexts = [],
    classNamesMobileOverrides,
    classNames,
    mobileDynamicClassesOverrides,
    baseDynamicClasses,
    tracker: _tracker,
    buttons = [],
    id,
    sectionType,
    trackingMetaData = {},
    numberOfProductsToShowInDesktop = DEFAULT_PRODUCTS_LIMIT_FOR_DESKTOP,
    numberOfProductsToShowInMobile = DEFAULT_PRODUCTS_LIMIT_FOR_MOBILE,
  } = sectionConfig ?? {}

  // Product data paired 1:1 with renderedChildren by original index (a nested widget without
  // productItem contributes no slide), then capped to the device limit like the non-PL section.
  const cardEntries = useMemo(
    () =>
      (Array.isArray(nestedWidgets) ? nestedWidgets : []).flatMap((widget, index) => {
        const product = widget.data?.productItem
        const node = renderedChildren?.[index]?.node
        return product && node ? [{ product, node }] : []
      }),
    [nestedWidgets, renderedChildren],
  )
  const PRODUCTS_LIMIT = isDesktop ? numberOfProductsToShowInDesktop : numberOfProductsToShowInMobile
  const visibleEntries = useMemo(() => cardEntries.slice(0, PRODUCTS_LIMIT), [cardEntries, PRODUCTS_LIMIT])

  const { styleObject, className } = useDynamicStyle({
    dynamicValueClassesMobileOverride: mobileDynamicClassesOverrides,
    baseDynamicValueClasses: baseDynamicClasses,
    mobileClassNamesOverride: classNamesMobileOverrides,
    baseClassNames: classNames,
    postFix: styles.dynamicClassesPostFix ?? '',
    wrapperClassName: styles.dynamic_class_wrapper,
  })

  const contentContainerTrackingMetadata = useMemo(
    () => getContentContainerTrackingMetadata(trackingMetaData),
    [trackingMetaData],
  )

  // Both non-PL branches suffix the trackers with `__personalised_product_feed`.
  const productViewTracker = useMemo(() => {
    if (!_tracker) return undefined
    const context = trackingMetaData.context
    // Truthiness (`||`) on purpose, matching the non-PL forks: an empty-string sectionType falls
    // through to `label` (CustomSection types sectionType as `CustomSectionType | ''`).
    const sectionSuffix =
      context && (Boolean(context.sectionType) || Boolean(context.label))
        ? `__${context.sectionType}__${context.label}`
        : ''
    return `${_tracker}${sectionSuffix}__personalised_product_feed`
  }, [_tracker, trackingMetaData])

  const getIsVisibleTrackParams = useCallback(
    () => ({
      ...trackingMetaData,
      action: `${_tracker}__personalised_product_feed`,
      // @i18n-ignore
      value: 'true',
      // @i18n-ignore
      category: 'scroll_into_view',
    }),
    [trackingMetaData, _tracker],
  )
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  useBatchedAnalyticsForProductCard({
    productViewTracker,
    trackingMetaData,
  })

  // Each visible pre-rendered SINGLE_PRODUCT_CARD node becomes a grid cell, wrapped in its own
  // CardTrackingProvider carrying the EXACT per-card click/wishlist configs the non-PL section
  // builds inside `renderProduct` — `ProductCard` emits PRODUCT_CLICK / sets findingMethod / adds
  // link attribution only when these are present, so without them the home cards would go silent.
  // The provider also carries the section view tracker (so impressions land on this section's
  // aggregator, not the page one). A single CardDisplayOptionsProvider around the grid supplies
  // the 6-up-grid `defaultSM` DS variant; display flags keep their show-everything defaults
  // (matches non-PL). Memoized so an unrelated context update doesn't rebuild every card's
  // provider value; `findingMethod` is consequently snapshotted per memo run rather than per
  // render — same value at click time in practice, since navigation that changes it also
  // re-renders the page.
  const slides = useMemo(
    () => (
      // eslint-disable-next-line i18next/no-literal-string -- DS tile variant token, not user-facing copy
      <CardDisplayOptionsProvider dsVariant="defaultSM" cardProps={HOME_TILE_CHROME_PROPS}>
        {visibleEntries.map((entry, idx) => {
          const clickTracker = `${_tracker}__click_personalised_product_feed`
          const trackingContext = {
            ...getTrackingContext(clickTracker, ''),
            label: clickTracker,
          }
          const productClickTrackConfig = {
            trackInternal: true,
            trackGA: true,
            ...trackingContext,
            context: {
              sectionType,
              sectionid: id,
              productPosition: idx,
              ...trackingMetaData.context,
              ...contentContainerTrackingMetadata,
              findingMethod: getFindingMethodAggregate(),
            },
          }
          const wishlistTrackConfig = {
            ...trackingContext,
            sectionType,
            sectionid: id,
            productPosition: idx,
            ...trackingMetaData.context,
            ...contentContainerTrackingMetadata,
            findingMethod: getFindingMethodAggregate(),
          }
          return (
            <CardTrackingProvider
              key={String(entry.product.productId)}
              tracker={`${_tracker}__personalised_product_feed`}
              productClickTrackConfig={productClickTrackConfig}
              wishlistTrackConfig={wishlistTrackConfig}
              productViewTracker={productViewTracker}
            >
              {entry.node}
            </CardTrackingProvider>
          )
        })}
      </CardDisplayOptionsProvider>
    ),
    [visibleEntries, _tracker, sectionType, id, trackingMetaData, contentContainerTrackingMetadata, productViewTracker],
  )

  if (!data || !sectionConfig) return null
  if (!visibleEntries.length) return null

  const headerButtonProps = ButtonDomain.makeButtonProps(buttons[0])
  const headerTrackConfig = headerButtonProps && {
    trackInternal: true,
    category: NAVIGATION,
    action: CLICK_SHOP_BUTTON,
    label: headerButtonProps.children as string,
    value: _tracker,
    context: { ...trackingMetaData.context },
  }
  const onHeaderButtonClick = () => {
    setFindingMethod[FINDING_METHOD_AGGREGATE.CAMPAIGN]?.({
      elementName: trackingMetaData.context?.contentContainerName ?? '',
      elementId: trackingMetaData.context?.trackingId ?? '',
    })
  }

  // DS branch — mirrors PersonalisedProductsDS (same exported classes, header CTA, 6-up grid,
  // test locator), arranging pre-rendered children instead of mapping data to cards.
  if (shouldUseDesignSystem()) {
    const LinkWithTracking = (linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
      <TrackedLink {...linkProps} trackConfig={headerTrackConfig ?? undefined} withoutBaseStyle hidePointerCursor />
    )

    const dsHeaderAction = headerButtonProps ? (
      isDesktop ? (
        <DSButton
          // eslint-disable-next-line i18next/no-literal-string -- DS Button variant token, not user-facing copy
          variant="tertiary"
          // eslint-disable-next-line i18next/no-literal-string -- DS Button size token, not user-facing copy
          size="sm"
          LinkComponent={LinkWithTracking}
          href={headerButtonProps.href}
          onClick={onHeaderButtonClick as React.MouseEventHandler<HTMLAnchorElement>}
        >
          {headerButtonProps.children}
        </DSButton>
      ) : (
        <LinkWithTracking
          href={headerButtonProps.href}
          onClick={onHeaderButtonClick as React.MouseEventHandler<HTMLAnchorElement>}
          aria-label={t('shop_now_v1')}
          className={chevronLinkClass}
        >
          <ChevronRightIcon width="20px" height="20px" aria-hidden />
        </LinkWithTracking>
      )
    ) : null

    return (
      <div ref={trackIsVisibleRef} className={sectionShellClass}>
        <div className={sectionInnerClass}>
          <div className={headerRowClass}>
            <div className={titleSlotClass} data-testid={TEST_LOCATORS.PERSONALISED_PRODUCTS.TITLE}>
              <RichText {...RichTextDomain.makeRichTextProps(richTexts[0])} />
            </div>
            {dsHeaderAction}
          </div>
          <div className={gridClass}>{slides}</div>
        </div>
      </div>
    )
  }

  // Legacy branch — mirrors PersonalisedProductsLegacy's chrome.
  const legacyHeaderButton = headerButtonProps ? (
    isDesktop ? (
      <Button {...headerButtonProps} trackConfig={headerTrackConfig} onClick={onHeaderButtonClick} />
    ) : (
      <Link
        href={headerButtonProps.href}
        trackConfig={headerTrackConfig}
        className={styles.arrowLinkContianer}
        onClick={onHeaderButtonClick}
        aria-label={t('shop_now_v1')}
      >
        <Icon iconSvg={RightArrowIcon} className={styles.arrow} />
      </Link>
    )
  ) : null

  return (
    <div ref={trackIsVisibleRef}>
      <div className={classnames(styles.container, className)} style={styleObject}>
        <div className={classnames(styles.headerWrapper)}>
          <RichText {...RichTextDomain.makeRichTextProps(richTexts[0])} />
          {legacyHeaderButton}
        </div>
        <div
          className={classnames(styles.child, styles.productsListContainer, {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            [styles.productsListSection!]: !isDesktop,
          })}
        >
          {slides}
        </div>
      </div>
    </div>
  )
}

export const PersonalisedProductsWidget: PLTypedWidgetInterface<
  PersonalisedProductsWidgetData,
  WidgetType.PERSONALISED_PRODUCTS
> = {
  id: WidgetType.PERSONALISED_PRODUCTS,
  errorBoundary: PLBaseErrorBoundary,
  render: PersonalisedProductsWidgetComponent,
}
